import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Form, Button, Dropdown, Icon } from 'semantic-ui-react';
import i18next from 'i18next';
import Moment from 'moment';
import { toastr } from 'react-redux-toastr';
import { extendMoment } from 'moment-range';
import { editEntityField, saveMarketingCampaign, fetchBrands, saveBrand, confirmDelete, deleteMarketingCampaign } from '../../../actions';
import { DateRangePicker } from '../../common';
import { randomHexColor } from '../../../helpers/random';
import { getCurrentChain, getChainRestaurants, getCurrentRestaurant } from '../../../selectors';

const moment = extendMoment(Moment);

const MarketingCampaignEditor = (props) => {
    const [newBrandKey, setNewBrandKey] = useState(false);
    const chain = useSelector(getCurrentChain);
    const restaurant = useSelector(getCurrentRestaurant);
    const restaurants = useSelector(getChainRestaurants);
    const campaigns = useSelector(state => state.businessApp.marketingCampaigns);
    const brands = useSelector(state => state.businessApp.brands);
    const entity = useSelector(state => state.editor.entity);
    const dispatch = useDispatch();

    const chainId = chain ? chain.id : restaurant.ownerId;
    const restId = restaurant ? restaurant.id : null;
    const marketingCampaign = entity || {};

    useEffect(() => {
        dispatch(fetchBrands(null, chainId));
    }, [chain]);

    const isCampaignDatesOverlaps = (campaign) => {
        const campaignDatesRange = moment.range(campaign.startDate, campaign.endDate);
        return campaigns.some(c => {
            const range = moment.range(c.startDate, c.endDate);
            return range.overlaps(campaignDatesRange);
        });
    };

    const isBrandKeyValid = (key) => {
        if (!key) return false;
        // eslint-disable-next-line no-useless-escape
        return !(/\`|\~|\!|\@|\#|\$|\%|\^|\&|\*|\(|\)|\+|\=|\[|\{|\]|\}|\||\\|\'|\<|\,|\.|\>|\?|\/|\""|\;|\:|[\u0590-\u05fe]/.test(key));
    };

    const saveCampaign = (marketingCampaign) => {
        const eventColor = marketingCampaign.id ? marketingCampaign.meta.color : randomHexColor();

        dispatch(saveMarketingCampaign(chainId, { ...marketingCampaign, chainId, meta: { ...marketingCampaign.meta, color: eventColor } }, restId, () => {
            props.handleSave(marketingCampaign);
        }));
    };

    const handleSubmit = (event) => {
        if (!marketingCampaign.id && isCampaignDatesOverlaps(marketingCampaign)) {
            toastr.error(i18next.t('marketingCampaigns.dateOverlaps'));
        } else {
            if (newBrandKey) {
                if (isBrandKeyValid(marketingCampaign.brandKey)) {
                    dispatch(saveBrand({ platform: marketingCampaign.platform, key: `campaign-${marketingCampaign.brandKey}`, chainId }, (brand) => {
                        saveCampaign({ ...marketingCampaign, brandKey: brand.key });
                    }));
                } else {
                    toastr.error(i18next.t('brands.brandKeyError'));
                }
            } else {
                saveCampaign(marketingCampaign);
            }
        }

        event.preventDefault();
    };

    const handleDelete = (marketingCampaign) => {
        dispatch(confirmDelete(marketingCampaign.title, () => {
            dispatch(deleteMarketingCampaign(chainId, restId, marketingCampaign));
            props.handleDelete();
        }));
    };

    return (
        <Form onSubmit={handleSubmit}>
            <Form.Field>
                <label>{i18next.t('name')}</label>
                <input
                    disabled={restId && props.isMultibranchCampaign(marketingCampaign)}
                    placeholder={i18next.t('name')}
                    value={marketingCampaign.title || ''}
                    onChange={({ target }) => dispatch(editEntityField('title', target.value))}
                    required
                />
            </Form.Field>

            <Form.Field>
                <Form.TextArea
                    disabled={restId && props.isMultibranchCampaign(marketingCampaign)}
                    label={i18next.t('marketingCampaigns.description')}
                    placeholder={i18next.t('marketingCampaigns.description')}
                    value={marketingCampaign.description || ''}
                    onChange={({ target }) => dispatch(editEntityField('description', target.value))}
                />
            </Form.Field>

            <Form.Field>
                <label style={{ padding: '0 5px' }}>{i18next.t('marketingCampaigns.datesRange')}</label>
                <DateRangePicker
                    disabled={restId && props.isMultibranchCampaign(marketingCampaign)}
                    value={[
                        moment(marketingCampaign.startDate),
                        moment(marketingCampaign.endDate)
                    ]}
                    onChange={(rangeDates) => {
                        const startDate = moment(rangeDates[0]);
                        const endDate = moment(rangeDates[1]);
                        dispatch(editEntityField('startDate', startDate));
                        dispatch(editEntityField('endDate', endDate));
                    }}
                    enableFutureDate
                />
            </Form.Field>

            <Form.Field>
                <label style={{ padding: '0 5px' }}>{i18next.t('marketingCampaigns.platform')}</label>
                <Dropdown
                    disabled={restId && props.isMultibranchCampaign(marketingCampaign)}
                    fluid
                    selection
                    options={[{ text: i18next.t('kiosk'), value: 'kiosk' }, { text: i18next.t('website.title'), value: 'website' }]}
                    onChange={(e, { value }) => dispatch(editEntityField('platform', value))}
                    value={marketingCampaign.platform}
                />
            </Form.Field>

            {chain && <Form.Field>
                <label style={{ padding: '0 5px' }}>{i18next.t('marketingCampaigns.branchIds')}</label>
                <Dropdown
                    fluid
                    search
                    selection
                    multiple
                    placeholder={i18next.t('marketingCampaigns.branchIdsPlaceholder')}
                    options={restaurants.map(rest => ({ text: `${rest.name}-${rest.id}`, value: rest.id }))}
                    onChange={(e, { value }) => dispatch(editEntityField('branchIds', value))}
                    value={marketingCampaign.branchIds || []}
                />
            </Form.Field>}

            <Form.Field>
                <label style={{ padding: '0 5px' }}>{i18next.t('marketingCampaigns.brand')}</label>
                <Dropdown
                    fluid
                    selection
                    required
                    disabled={!!marketingCampaign.id || restId && props.isMultibranchCampaign(marketingCampaign)}
                    placeholder={i18next.t('choose')}
                    options={[{ text: i18next.t('createNew'), value: 'createNew' }].concat(brands.map((brand, index) => ({ key: index, text: brand.key, value: brand.key })))}
                    onChange={(e, { value }) => {
                        if (value === 'createNew') {
                            setNewBrandKey(true);
                            dispatch(editEntityField('brandKey', ''));
                        } else {
                            setNewBrandKey(false);
                            dispatch(editEntityField('brandKey', value));
                        }
                    }}
                    value={newBrandKey ? 'createNew' : marketingCampaign.brandKey}
                />
            </Form.Field>

            {newBrandKey && (
                <Form.Field>
                    <label>{i18next.t('marketingCampaigns.brandKey')}</label>
                    <input
                        disabled={restId && props.isMultibranchCampaign(marketingCampaign)}
                        placeholder={i18next.t('marketingCampaigns.brandKeyPlaceholder')}
                        value={marketingCampaign.brandKey}
                        onChange={({ target }) => {
                            dispatch(editEntityField('brandKey', target.value));
                        }}
                    />
                </Form.Field>
            )}

            <Button primary type='submit' floated="right" disabled={restId && props.isMultibranchCampaign(marketingCampaign)}>
                {i18next.t('save')}
            </Button>
            {marketingCampaign.id && (
                <Button type="button" floated="right" icon negative onClick={() => handleDelete(marketingCampaign)}>
                    <Icon name="trash" />{i18next.t('delete')}
                </Button>
            )}
        </Form>
    );
};

MarketingCampaignEditor.propTypes = {
    saveChain: PropTypes.func,
    editEntityField: PropTypes.func,
    isMultibranchCampaign: PropTypes.func,
    handleSave: PropTypes.func,
    handleDelete: PropTypes.func,
    entity: PropTypes.object,
};

export default MarketingCampaignEditor;
